import type { CopilotActionView } from "@/features/copilot/schemas/copilot.schema";

const statusLabels: Record<CopilotActionView["status"], string> = {
  pending: "Waiting for your decision",
  running: "Applying this change",
  confirmed: "Completed",
  declined: "Declined",
  expired: "Expired",
  failed: "Could not complete",
  unknown: "Result not confirmed",
};

export function copilotActionStatusLabel(status: CopilotActionView["status"]): string {
  return statusLabels[status];
}

export interface CopilotActionField {
  label: string;
  value: string;
}

/// Labels for argument names whose camelCase reading is not what a person would call them.
const FIELD_LABELS: Record<string, string> = {
  text: "Message",
  body: "Message",
  to: "To",
  userId: "Person",
  personId: "Person",
  membershipId: "Person",
  assigneeIds: "Assignees",
};

const ID_SHAPED =
  /^(?:[a-z0-9]{20,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

function labelFor(key: string): string {
  const known = FIELD_LABELS[key];
  if (known) return known;
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/// The frozen arguments as the person reads them before confirming: every content field, labelled,
/// with ids replaced by the names the server resolved. Ids that resolve to nothing are bookkeeping
/// rather than content, so they go to `technical` instead of being dropped. So do structured
/// arguments.
export function copilotActionFields(action: CopilotActionView): {
  primary: CopilotActionField[];
  technical: CopilotActionField[];
} {
  const names = new Map(action.summary.targets.map((target) => [target.id, target.label]));
  const primary: CopilotActionField[] = [];
  const technical: CopilotActionField[] = [];
  const unresolvedId = (value: unknown) =>
    typeof value === "string" && ID_SHAPED.test(value) && !names.has(value);
  const show = (value: unknown): string => {
    if (typeof value === "string") return names.get(value) ?? value;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null) return "None";
    if (Array.isArray(value)) return value.map(show).join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };
  // Structured arguments (plan lines, nested objects) have no readable form here: the summary's
  // facts say it in words, so the raw JSON is only kept for anyone who wants to inspect it. Without
  // facts the fields are the only account of the action, so they stay in view.
  const hasFacts = action.summary.facts !== undefined;
  const structured = (value: unknown) =>
    hasFacts &&
    ((typeof value === "object" && value !== null && !Array.isArray(value)) ||
      (Array.isArray(value) && value.some((item) => typeof item === "object" && item !== null)));
  for (const [key, value] of Object.entries(action.args)) {
    // Optimistic-concurrency marker: it guards the write, it is not something a person reviews.
    if (key === "version") continue;
    const bookkeeping =
      structured(value) ||
      unresolvedId(value) ||
      (Array.isArray(value) && value.length > 0 && value.every(unresolvedId));
    (bookkeeping ? technical : primary).push({ label: labelFor(key), value: show(value) });
  }
  return { primary, technical };
}

export function copilotActionDecisionEnabled({
  approvalRequested,
  approvalSending,
  serverWaiting,
  runtimeIsIdle,
  siblingExecuting,
  status,
}: {
  approvalRequested: boolean;
  approvalSending: boolean;
  serverWaiting: boolean;
  runtimeIsIdle: boolean;
  siblingExecuting: boolean;
  status: CopilotActionView["status"];
}): boolean {
  return (
    approvalRequested &&
    !approvalSending &&
    serverWaiting &&
    runtimeIsIdle &&
    !siblingExecuting &&
    status === "pending"
  );
}

/// Whether the change is still ahead of the person or has landed; after a decline, an expiry or a
/// failure nothing happened, so there is no consequence or notification to speak of.
function actionTense(status: CopilotActionView["status"]): "pending" | "confirmed" | null {
  if (status === "pending" || status === "running") return "pending";
  return status === "confirmed" ? "confirmed" : null;
}

interface NoticeWording {
  pending: (names: string, plural: boolean) => string;
  confirmed: (names: string, plural: boolean) => string;
  /// What cannot be taken back, for the undo caveat.
  irreversible: string;
}

const verb = (plural: boolean, singular: string, many: string) => (plural ? many : singular);

const notifyTargets: NoticeWording = {
  pending: (names, plural) =>
    `${names} ${verb(plural, "is", "are")} notified as soon as you confirm.`,
  confirmed: (names, plural) => `${names} ${verb(plural, "was", "were")} notified.`,
  irreversible: "notification",
};

const notifyAssignees: NoticeWording = {
  pending: () => "Anyone assigned is notified as soon as you confirm.",
  confirmed: () => "Anyone assigned was notified.",
  irreversible: "notification",
};

/// Mirrors the catalog wording and the shore's notification bootstrappers: a capability only gets
/// a line here when something is actually sent. Unknown capabilities get no line rather than a guess.
const NOTICES: Record<string, NoticeWording> = {
  "feedback.create": notifyTargets,
  "kudos.create": {
    pending: (names, plural) =>
      `${names} ${verb(plural, "is", "are")} notified and everyone can see it as soon as you confirm.`,
    confirmed: (names, plural) =>
      `${names} ${verb(plural, "was", "were")} notified and everyone can see it.`,
    irreversible: "notification",
  },
  "todos.create": notifyAssignees,
  "goals.create": notifyAssignees,
  "goals.checkIns.create": {
    pending: () => "The goal's managers are notified as soon as you confirm.",
    confirmed: () => "The goal's managers were notified.",
    irreversible: "notification",
  },
  "leave.applications.create": {
    pending: () => "Your approver is notified as soon as you confirm.",
    confirmed: () => "Your approver was notified.",
    irreversible: "notification",
  },
  "leave.applications.decide": {
    pending: () => "The person who asked is notified of your decision.",
    confirmed: () => "The person who asked was notified.",
    irreversible: "notification",
  },
  "pulse.actions.create": {
    pending: () => "The action's owner is notified as soon as you confirm.",
    confirmed: () => "The action's owner was notified.",
    irreversible: "notification",
  },
  "people.invitations.create": {
    pending: () => "An invitation email is sent as soon as you confirm.",
    confirmed: () => "The invitation email was sent.",
    irreversible: "email",
  },
};

export function copilotActionNotice(action: CopilotActionView): string | null {
  const tense = actionTense(action.status);
  const wording = NOTICES[action.capability];
  if (!tense || !wording) return null;
  const labels = action.summary.targets.map((target) => target.label);
  const names = labels.length > 0 ? labels.join(", ") : "They";
  return wording[tense](names, labels.length !== 1);
}

/// Undo wording comes from the undo capability itself, not from the backend's consequence text, so
/// the card promises exactly what Hand can do. Decisions and changes with no undo keep the
/// backend's text, which is where the "final" wording lives.
export function copilotActionConsequence(action: CopilotActionView): string | null {
  const tense = actionTense(action.status);
  if (!tense) return null;
  if (action.risk !== "safe_write" || !action.summary.undoCapability) {
    return action.summary.consequence;
  }
  const undo = tense === "pending" ? "To undo it later, ask Hand." : "To undo it, ask Hand.";
  const wording = NOTICES[action.capability];
  return wording ? `${undo} The ${wording.irreversible} can't be taken back.` : undo;
}

/// The card title is a heading so the composer's "Review" button can move focus to it.
export function copilotActionTitleId(toolCallId: string): string {
  return `copilot-action-${toolCallId}`;
}
